import { useMemo } from "react";
import {
  useMobileMenu,
  usePlatformStore,
  useMarketingComputed,
} from "@platform/store";
import { useHandleNavigationClick } from "@core/features/mobileMenu";
import {
  useFetchActions,
  getOrganizationsFromActions,
  ACTION_TYPE_SLUG,
  type Action,
} from "@core/features/actions";
import { MarketingNavigationItems } from "./MarketingNavigationItems";

export function MarketingNavigationItemsContainer() {
  const user = usePlatformStore((s) => s.user);
  const isSupportPageDisabled = usePlatformStore(
    (s) => s.featureFlags.isSupportPageDisabled
  );
  const { showLoginCta } = useMarketingComputed();

  const { close: closeMobileMenu } = useMobileMenu();

  const { handleNavigationAnchorClick, handleNavigationClick } =
    useHandleNavigationClick({ closeMobileMenu });

  // Fetch user actions to build dynamic menu items
  const { data: actionsData } = useFetchActions({
    user,
    isEnabled: !!user,
  });

  const menuItems = useMemo(() => {
    const items: { title: string; href: string }[] = [
      { title: "Dashboard", href: "/dashboard" },
    ];

    if (!actionsData?.actions) {
      const fallbackItems = [...items];
      fallbackItems.push({ title: "Settings", href: "/account" });
      fallbackItems.push({ title: "Logout", href: "/logout" });
      return fallbackItems;
    }

    const actions = actionsData.actions;

    // Program (if has schedule or circle_view action)
    const programAction = actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.SCHEDULE ||
        action.action_type?.slug === ACTION_TYPE_SLUG.CIRCLE_VIEW
    );
    if (programAction?.program) {
      items.push({
        title: "Program",
        href: `/program/${programAction.program}`,
      });
    }

    // Profile (if has any actions with a program)
    const firstActionWithProgram = actions.find(
      (action: Action) => action.program
    );
    if (firstActionWithProgram?.program) {
      items.push({
        title: "Profile",
        href: `/program/${firstActionWithProgram.program}/profile`,
      });
    }

    // Invitations (if has send_invitation action)
    const invitationAction = actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.SEND_INVITATION
    );
    if (invitationAction?.program) {
      items.push({
        title: "Invitations",
        href: `/program/${invitationAction.program}/invitations`,
      });
    }

    // Resources (if has resource actions)
    const resourceAction = actions.find(
      (action: Action) =>
        action.action_type?.slug === ACTION_TYPE_SLUG.WORKBOOK_DOWNLOAD ||
        action.action_type?.slug === ACTION_TYPE_SLUG.SOCIALMEDIAKIT_DOWNLOAD
    );
    if (resourceAction?.program) {
      items.push({
        title: "Resources",
        href: `/program/${resourceAction.program}/resources`,
      });
    }

    // Support (when not disabled, requires a program)
    if (!isSupportPageDisabled) {
      const firstProgramId = actions[0]?.program;
      if (firstProgramId) {
        items.push({ title: "Support", href: `/program/${firstProgramId}/support` });
      }
    }

    // Organization Admin (if has org admin actions)
    const organizations = getOrganizationsFromActions(actions);
    for (const org of organizations) {
      items.push({
        title: org.name ?? "Organization Admin",
        href: `/program/${org.programId}/organization/${org.id}/admin`,
      });
    }

    // Settings and Logout (always)
    items.push({ title: "Settings", href: "/account" });
    items.push({ title: "Logout", href: "/logout" });

    return items;
  }, [actionsData?.actions, isSupportPageDisabled]);

  return (
    <MarketingNavigationItems
      onNavigationClick={handleNavigationClick}
      onNavigationAnchorClick={handleNavigationAnchorClick}
      user={user}
      showLoginCta={showLoginCta}
      menuItems={menuItems}
    />
  );
}

export default MarketingNavigationItemsContainer;
